import { queryOptions, skipToken, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { parseTemplate, type Template } from '../template/entity';
import { parseTransactionalMessage, transactionalMessageKeys, type TransactionalMessage } from './entity';

const STALE_TIME = 60_000;

/** The unpaginated transactional-messages list, with the templates the rows point at. */
export interface TransactionalMessagesList {
  transactionalMessages: TransactionalMessage[];
  templates: Template[];
}

/**
 * Workspace transactional-messages list. Each row is sideloaded into its
 * detail slot so a follow-up `useTransactionalMessageOptional` resolves from
 * cache.
 *
 * @public
 */
export async function getTransactionalMessages(
  workspaceId: number,
  qc: QueryClient,
): Promise<TransactionalMessagesList> {
  const raw = await hydraUiApi.transactional.getTransactionalMessages({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const transactionalMessages = (raw.transactional_messages ?? []).map(parseTransactionalMessage);
  for (const message of transactionalMessages) {
    qc.setQueryData(transactionalMessageKeys.detail(workspaceId, message.id), message);
  }
  return { transactionalMessages, templates: raw.templates.map(parseTemplate) };
}

/** @public */
export const getTransactionalMessagesQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: transactionalMessageKeys.list(workspaceId, undefined),
    queryFn: () => getTransactionalMessages(workspaceId, qc),
    staleTime: STALE_TIME,
  });

/** @public */
export async function getTransactionalMessage(
  workspaceId: number,
  transactionalMessageId: string,
  qc: QueryClient,
): Promise<TransactionalMessage> {
  const raw = await hydraUiApi.transactional.getTransactionalMessagesByTransactionalMessage({
    params: { path: { environment_id: String(workspaceId), transactional_message_id: transactionalMessageId } },
  });
  if (raw.transactional_message == null) {
    throw new Error(`transactional message ${transactionalMessageId} not found`);
  }
  const parsed = parseTransactionalMessage(raw.transactional_message);
  qc.setQueryData(transactionalMessageKeys.detail(workspaceId, parsed.id), parsed);
  return parsed;
}

/** @public */
export const getTransactionalMessageQueryOptions = (
  workspaceId: number,
  transactionalMessageId: string,
  qc: QueryClient,
) =>
  queryOptions({
    queryKey: transactionalMessageKeys.detail(workspaceId, transactionalMessageId),
    queryFn: () => getTransactionalMessage(workspaceId, transactionalMessageId, qc),
    staleTime: STALE_TIME,
  });

interface UseTransactionalMessageOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled by-id hook (see `useSnippetOptional`).
 *
 * @public
 */
export const useTransactionalMessageOptional = ({
  workspaceId,
  id,
  enabled = true,
}: UseTransactionalMessageOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? transactionalMessageKeys.detail(workspaceId, id) : ['transactional_message', 'inactive'],
    queryFn: active ? () => getTransactionalMessage(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};
